import React, { useState, ChangeEvent, FunctionComponent } from 'react';
import { NumericFormat } from 'react-number-format';
import { InputAdornment, IconButton } from '@mui/material';
import { DeleteOutlined, VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material';
import i18next from 'i18next';

import { FILE_TYPES_ARRAY } from 'helpers/validations/constants';

import { FormGroup, ErrorBlock, HelpText } from 'styles/input';

import { IInputProps } from './types';
import { CustomInput, FilePreviewLinkContainer, FilePreviewLink } from './styles';

import './i18n';

const Input: FunctionComponent<IInputProps> = (props: IInputProps) => {
  const {
    className = '',
    disabled = false,
    hasNumericFormat = false,
    helpText = '',
    input,
    label = '',
    maxLength = 255,
    maxRows = 3,
    minRows = 3,
    meta: { touched, error },
    onDeleteFile,
    previewFiles = [],
    size = 'medium',
    variant = 'filled',
  } = props;
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { multiple, name, onChange, type = 'text', value } = input;
  const isColorInput = type === 'color';
  const isDateInput = type === 'date' || type === 'datetime' || type === 'datetime-local';
  const isFileInput = type === 'file';
  const isNumberInput = type === 'number';
  const isPasswordInput = type === 'password';
  const isTextAreaInput = type === 'textarea';
  const hasError = !!touched && !!error;

  const inputLabelProps =
    isColorInput || isDateInput || isFileInput ? { focused: true, shrink: true } : {};
  const inputFileProps = isFileInput ? { accept: FILE_TYPES_ARRAY.join(','), multiple } : {};

  const getSelectedFileLabel = (): string => {
    if (!isFileInput || !value) return '';

    if (value instanceof File) return value.name;

    if (typeof FileList !== 'undefined' && value instanceof FileList) {
      return Array.from(value)
        .map((f) => f.name)
        .join(', ');
    }

    if (Array.isArray(value) && value.length && value[0] instanceof File) {
      return (value as File[]).map((f) => f.name).join(', ');
    }

    return '';
  };

  const getInputPlaceholder = () => {
    if (isFileInput) {
      return multiple
        ? i18next.t<string>('INPUT:FILE_MULTIPLE_PLACEHOLDER')
        : i18next.t<string>('INPUT:FILE_PLACEHOLDER');
    }
    return undefined;
  };

  const getInputType = () => {
    let inputType = type;
    if (isFileInput || (isPasswordInput && showPassword)) {
      inputType = 'text';
    }
    return inputType;
  };

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (isFileInput && files?.length) onChange(multiple ? files : files[0]);
    else onChange(event.target.value);
  };

  const inputTextProps = {
    ...input,
    disabled,
    error: hasError,
    id: name,
    inputProps: {
      'aria-label': name,
      'data-testid': `input-${name}`,
      maxLength,
      ...inputFileProps,
    },
    InputLabelProps: { ...inputLabelProps },
    label,
    maxRows,
    minRows,
    multiline: isTextAreaInput,
    name,
    onChange: onInputChange,
    placeholder: getInputPlaceholder(),
    size,
    type: getInputType(),
    value: isFileInput ? getSelectedFileLabel() : (value as string),
    variant,
    // eslint-disable-next-line react/jsx-no-duplicate-props
    InputProps: {
      readOnly: isFileInput,
      endAdornment: isPasswordInput && !disabled && (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle password visibility"
            data-testid="toggle-password-button"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
          </IconButton>
        </InputAdornment>
      ),
    },
  };

  const inputNumberProps = {
    ...inputTextProps,
    customInput: CustomInput,
    decimalScale: 2,
    fixedDecimalScale: true,
    onChange: undefined,
    onValueChange: ({ floatValue }: { floatValue?: number }) =>
      onChange(floatValue?.toString() || ''),
    thousandSeparator: true,
    valueIsNumericString: false,
  };

  return (
    <FormGroup className={className}>
      {isNumberInput && hasNumericFormat ? (
        <NumericFormat {...inputNumberProps} type="text" />
      ) : (
        <>
          <CustomInput {...inputTextProps} />
          {isFileInput && (
            <input
              id={name}
              name={name}
              type="file"
              multiple={multiple}
              accept={FILE_TYPES_ARRAY.join(',')}
              onChange={onInputChange}
              disabled={disabled}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                cursor: disabled ? 'default' : 'pointer',
              }}
            />
          )}
        </>
      )}
      {!!helpText && <HelpText data-testid="help-text">{helpText}</HelpText>}
      {hasError && <ErrorBlock data-testid="error-block">{error}</ErrorBlock>}
      {isFileInput &&
        previewFiles.map(
          (file) =>
            file.value && (
              <FilePreviewLinkContainer
                key={`file-${file.value}`}
                positionAbsolute={previewFiles.length === 1 && !onDeleteFile}
              >
                <FilePreviewLink
                  download={file.label}
                  href={file.value}
                  rel="noreferrer"
                  target="_blank"
                >
                  {file.label}
                </FilePreviewLink>
                {onDeleteFile && (
                  <IconButton onClick={() => onDeleteFile(file)}>
                    <DeleteOutlined />
                  </IconButton>
                )}
              </FilePreviewLinkContainer>
            ),
        )}
    </FormGroup>
  );
};

export default Input;
